import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isEqual } from 'lodash';
import { ReportIcon } from 'src/components/ui-icons/sidebar';
import { useNavigate } from 'react-router-dom';
import FilterForPlanAction from 'src/pages/DashboardPage/vpo/components/planAction/filterForPlanAction/FilterForPlanAction';
import { filterConfigPeriodGop, filtersOpenPeriodGop, getInitialFilterPeriodGop, months, years } from './forFilterData';
import TableRowGop from './TableRowGop';
import { StatusDoneGop } from './StatusDoneGop';
import { StatisticCardGop } from './StatisticCardGop';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { selectModelData, selectModelLoading } from '../../selectors/selectors';
import { fetchData } from '../../actions/actions';
import Loading from 'src/components/Loading';
import { transformFiltersForCompleteEndpoint } from '../../utils/reportPeriod';
import {
  getFactoriesForSelectedGroups,
  getFinalFilteredFactories,
  getGOPsForSelectedGroups,
  transformDataForFilters,
  transformDataForFiltersGOPs,
  transformDataForFiltersInCode,
} from '../../utils/reports';
import { fetchReportDataRequest } from '../../store/reports/actions';
import { selectReportAnswers, selectReportCombinedLoading, selectReportStatuses } from '../../store/reports/selectors';

export default function ReportOffPeriod() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [currentFilters, setCurrentFilters] = useState<any>(null);
  const [filtersLoading, setFiltersLoading] = useState<boolean>(true);
  const [filtersInitialized, setFiltersInitialized] = useState<boolean>(false);
  const [baseInitialFilters, setBaseInitialFilters] = useState<any>(null);
  const [requestSent, setRequestSent] = useState<boolean>(false);
  const lastFiltersRef = useRef<string>('');
  const allAnswers = useAppSelector(selectReportAnswers);
  const allStatuses = useAppSelector(selectReportStatuses);
  const dataLoading = useAppSelector(selectReportCombinedLoading);
  const groupGop = useAppSelector((state: any) => selectModelData(state, 'gop-groups'));
  const factoriesGop = useAppSelector((state: any) => selectModelData(state, 'gop-factories'));
  const formGops = useAppSelector((state: any) => selectModelData(state, 'gop-gops'));
  const gopDirection = useAppSelector((state: any) => selectModelData(state, 'gop-directions'));
  const factoriesLoading = useAppSelector((state: any) => selectModelLoading(state, 'gop-factories'));
  const directionsLoading = useAppSelector((state: any) => selectModelLoading(state, 'gop-directions'));
  const gopsLoading = useAppSelector((state: any) => selectModelLoading(state, 'gop-gops'));
  const listNames = ['gop-groups'];

  useEffect(() => {
    listNames.forEach((listName) => {
      dispatch(fetchData(listName));
    });
  }, [dispatch]);

  useEffect(() => {
    const selectedGroupIds = currentFilters?.groupGop?.selectedIds;
    dispatch(
      fetchData(
        'gop-gops',
        {
          group: selectedGroupIds,
        },
        {
          page: 1,
          pageSize: 100,
        }
      )
    );
  }, [dispatch, currentFilters?.groupGop?.selectedIds]);

  useEffect(() => {
    const selectedGopIds = currentFilters?.formGop?.selectedIds;

    let groupIdsFromSelectedGops: number[] = [];
    if (selectedGopIds && selectedGopIds.length > 0 && formGops && formGops.length > 0) {
      formGops.forEach((gop: any) => {
        if (selectedGopIds.includes(gop.id) && gop.group) {
          if (Array.isArray(gop.group)) {
            gop.group.forEach((g: any) => {
              if (!groupIdsFromSelectedGops.includes(g.id)) {
                groupIdsFromSelectedGops.push(g.id);
              }
            });
          } else if (gop.group.id && !groupIdsFromSelectedGops.includes(gop.group.id)) {
            groupIdsFromSelectedGops.push(gop.group.id);
          }
        }
      });
    }

    dispatch(
      fetchData(
        'gop-factories',
        {
          group: groupIdsFromSelectedGops.length > 0 ? groupIdsFromSelectedGops : undefined,
        },
        {
          page: 1,
          pageSize: 100,
        }
      )
    );
  }, [dispatch, currentFilters?.formGop?.selectedIds, formGops]);

  useEffect(() => {
    const selectedGopIds = currentFilters?.formGop?.selectedIds;
    if (selectedGopIds && selectedGopIds.length > 0) {
      dispatch(fetchData('gop-directions', { gop: selectedGopIds }));
    } else if (currentFilters) {
      dispatch(fetchData('gop-directions'));
    }
  }, [dispatch, currentFilters?.formGop?.selectedIds]);

  useEffect(() => {
    if (formGops && formGops.length > 0 && !filtersInitialized) {
      const dynamicInitialFilter = getInitialFilterPeriodGop(formGops);
      setCurrentFilters(dynamicInitialFilter);
      setBaseInitialFilters(dynamicInitialFilter);
      setFiltersInitialized(true);
      setFiltersLoading(false);
    }
  }, [formGops, filtersInitialized]);

  useEffect(() => {
    if (!currentFilters || !filtersInitialized || !gopDirection || !factoriesGop) {
      return;
    }
    if (dataLoading) {
      return;
    }
    if (factoriesLoading || directionsLoading || gopsLoading) {
      return;
    }
    const selectedGopIds = currentFilters?.formGop?.selectedIds;
    if (selectedGopIds && selectedGopIds.length > 0) {
      const directionsMatchSelectedGop = gopDirection.every((direction: any) => {
        return direction.gop && direction.gop.some((gopItem: any) => selectedGopIds.includes(gopItem.id));
      });

      if (!directionsMatchSelectedGop) {
        return;
      }
    }

    const filtersKey = JSON.stringify({
      ...currentFilters,
      gopDirectionsLength: gopDirection.length,
    });

    if (lastFiltersRef.current === filtersKey) {
      return;
    }
    const availableFactories = getFactoriesForSelectedGroups(currentFilters, groupGop, factoriesGop);
    const transformedGopDirections = transformDataForFiltersInCode(gopDirection);
    const transformedGop = transformDataForFilters(formGops);

    if (!availableFactories?.length || !transformedGopDirections?.length) {
      return;
    }

    const apiFilters = transformFiltersForCompleteEndpoint(currentFilters, months, availableFactories, transformedGopDirections, transformedGop);

    const requiredParams = ['factories', 'quarters', 'years', 'directions'];
    const missingParams = requiredParams.filter((param) => !apiFilters[param] || apiFilters[param].length === 0);

    if (missingParams.length > 0) {
      return;
    }

    lastFiltersRef.current = filtersKey;
    setRequestSent(true);
    dispatch(fetchReportDataRequest(apiFilters));
  }, [currentFilters, filtersInitialized, gopDirection, factoriesGop, groupGop, dataLoading, factoriesLoading, directionsLoading, gopsLoading]);

  useEffect(() => {
    if (!dataLoading && requestSent) {
      setRequestSent(false);
    }
  }, [dataLoading, requestSent]);

  const handleFiltersChange = useCallback(
    (newFilters: React.SetStateAction<any>) => {
      if (currentFilters && !isEqual(currentFilters, newFilters)) {
        setRequestSent(true);
        lastFiltersRef.current = '';
        setCurrentFilters(newFilters);
      }
    },
    [currentFilters]
  );

  const availableFactories = React.useMemo(() => getFactoriesForSelectedGroups(currentFilters, groupGop, factoriesGop), [currentFilters, groupGop, factoriesGop]);
  const UiFilterFactory = React.useMemo(() => getFinalFilteredFactories(currentFilters, groupGop, factoriesGop), [currentFilters, groupGop, factoriesGop]);
  const availableGOPs = React.useMemo(() => getGOPsForSelectedGroups(currentFilters, groupGop, formGops), [currentFilters, groupGop, formGops]);
  const transformedGopDirections = React.useMemo(() => transformDataForFiltersInCode(gopDirection), [gopDirection]);
  const transformedFormGops = React.useMemo(() => transformDataForFiltersGOPs(availableGOPs), [availableGOPs]);
  const transformedFactoriesGop = React.useMemo(() => transformDataForFilters(availableFactories), [availableFactories]);
  const transformedGroupGop = React.useMemo(() => transformDataForFilters(groupGop), [groupGop]);

  const filterConfigs = {
    year: { items: years },
    month: { items: months },
    formGop: { items: transformedFormGops },
    factoryGop: { items: transformedFactoriesGop },
    groupGop: { items: transformedGroupGop },
  };

  const filtersMap = {
    year: years,
    month: months,
    formGop: transformedFormGops,
    factoryGop: transformedFactoriesGop,
    groupGop: transformedGroupGop,
  };

  if (filtersLoading || !filtersInitialized) {
    return (
      <div className='flex justify-between'>
        <div className='w-[320px] mr-2 bg-white'>
          <div className='p-4'>
            <Loading />
          </div>
        </div>
        <div className='flex-1 min-w-0 m-4'>
          <div className='flex items-center justify-center h-64'>
            <Loading />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='flex justify-between'>
      <div className='w-[320px] mr-2 bg-white'>
        <FilterForPlanAction
          onFiltersChange={handleFiltersChange}
          filterConfigs={filterConfigs}
          filtersMap={filtersMap}
          initialFilters={baseInitialFilters || currentFilters}
          filtersOpen={filtersOpenPeriodGop}
          filtersConfig={filterConfigPeriodGop}
          responsibleFil={false}
          selectedGropu={true}
          heightFilter={'h-[calc(100vh-2px)]'}
        />
      </div>

      <div className='flex-1 min-w-0 m-4'>
        <div className='flex items-center justify-between mb-6 font-normal text-primary'>
          <div className='flex items-center'>
            <span className='pr-3'>
              <ReportIcon />
            </span>
            <span className='cursor-pointer' onClick={() => navigate('/dashboard/2/reports')}>
              Отчеты
            </span>
            <span className='mx-2'>→</span>
            <span>Отчет по закрытию периода самооценки</span>
          </div>
          <div className='text-right truncate max-w-[20%]'>{currentFilters?.formGop?.selected?.[0]}</div>
        </div>

        {dataLoading || requestSent || factoriesLoading || directionsLoading || gopsLoading || !gopDirection || gopDirection.length === 0 ? (
          <>
            <div className='mb-6 min-w-0'>
              <div className='bg-white rounded-lg shadow p-8'>
                <Loading />
              </div>
            </div>
            <div className='mb-6 min-w-0'>
              <div className='bg-white rounded-lg shadow p-8'>
                <Loading />
              </div>
            </div>
            <div className='bg-white rounded-lg shadow min-w-0'>
              <div className='p-8'>
                <Loading />
              </div>
            </div>
          </>
        ) : (
          <>
            <div className='mb-6 min-w-0'>
              <StatisticCardGop FilteGroup={UiFilterFactory} GopDirections={transformedGopDirections} StatusData={allStatuses} />
            </div>
            <div className='mb-6 min-w-0'>
              <StatusDoneGop FilteGroup={UiFilterFactory} GopDirections={transformedGopDirections} StatusData={allStatuses} />
            </div>
            <div className='bg-white rounded-lg shadow min-w-0'>
              <TableRowGop FilteGroup={UiFilterFactory} VPOStatus={allAnswers} GopDirections={transformedGopDirections} StatusData={allStatuses} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
